import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { H2 } from '#app/components/typography/h2.tsx'
import { CodeBlock } from '#app/old_components/ui/code-block.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Visually Hidden',
}

const component: componentProps = {
	name: 'Visually Hidden',
	shortName: 'VisuallyHidden',
	fileName: 'visually-hidden',
	category: 'layout',
	description: ['Hides its children visually, while keeping content visible to screen readers.'],
	features: ['Visually hides content while preserving it for assistive technology.'],
	parts: [
		{
			name: 'VisuallyHidden',
			description: 'Hides its children visually, while keeping content visible to screen readers.',
		},
	],
	usage: `<Button>
	<Icon name="bars-3" />
	<VisuallyHidden>Open sidebar</VisuallyHidden>
</Button>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const VisuallyHiddenRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Parts parts={parts} />
			<H2>Usage</H2>
			<CodeBlock code={usage} filename={`${shortName}Example`} extension="tsx" className="mt-4" />
			<Source source={source} fileName={fileName} />
		</>
	)
}
export default VisuallyHiddenRoute
